import React from 'react'
import { observer } from 'mobx-react'
import ScopeChangeRequestsViewModel from 'admin/scope-change-requests/ScopeChangeRequestsViewModel'
import { useDocumentTitle } from 'hooks/document-title'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import { ContentLayout } from 'components/ContentLayout'
import ScopeChangeRequestHeader from 'admin/scope-change-requests/components/ScopeChangeRequestHeader'
import { useActivation } from 'hooks/mobx-hooks'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import ScopeChangeRequestStatusFilter from 'admin/scope-change-requests/components/ScopeChangeRequestStatusFilter'
import ScopeChangeRequestTable from 'admin/scope-change-requests/components/ScopeChangeRequestTable'
import { TableSearchbar } from 'payouts/components/TableSearchbar'
import { TableHost, TableScrollingContainer } from 'components/Table'
import {
  SideSheet,
  SideSheetHostView,
  SideSheetMain,
} from 'components/SideSheet'
import ScopeChangeRequestDetailPanel from 'admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel'
import ScopeChangeRequestFinalizedDialog from '../ScopeChangeRequestFinalizedDialog'
import ScopeChangeRequestResolveDialog from '../ScopeChangeRequestResolveDialog'
import ScopeChangeRequestRejectDialog from '../ScopeChangeRequestRejectDialog'
import ScopeChangeRequestRejectOnBehalfOfClientDialog from '../ScopeChangeRequestRejectOnBehalfOfClientDialog'
import styles from './ScopeChangeRequests.sass'
import ScopeChangeEditCustomItemsDialog from '../ScopeChangeEditCustomItemsDialog'
import ScopeChangeEditInventoryItemsDialog from '../ScopeChangeEditInventoryItemsDialog'
import ScopeChangeRequestActionSelectionDialog from '../ScopeChangeRequestActionSelectionDialog'

interface ScopeChangeRequestsProps {
  vm: ScopeChangeRequestsViewModel
}

export default observer(function ScopeChangeRequests({
  vm,
}: ScopeChangeRequestsProps) {
  useDocumentTitle('Change Requests')
  useActivation(vm)

  return vm.activate.match({
    pending: () => <ContentLoading />,
    rejected: (err) => <ContentError error={err} />,
    resolved: () => <ScopeChangeRequestsContent vm={vm} />,
  })
})

const ScopeChangeRequestsContent = observer(function ChangeRequestsContent({
  vm,
}: ScopeChangeRequestsProps) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollOffset = useScrollOffsetSpring(scrollRef)

  return (
    <div className={styles.root} ref={scrollRef}>
      <ScopeChangeRequestHeader scrollOffset={scrollOffset} vm={vm} />
      <SideSheetHostView
        sheetVisible={vm.changeRequestDetailsVM?.showing}
        ref={scrollRef}
      >
        <SideSheetMain>
          <ScopeChangeRequestStatusFilter filter={vm.searcher.statusFilter} />
          <ContentLayout>
            <TableHost>
              <TableSearchbar
                searchText={vm.searcher.searchText}
                onSearchTextChange={vm.searcher.setSearchText}
              />
              <TableScrollingContainer>
                <ScopeChangeRequestTable vm={vm} />
              </TableScrollingContainer>
            </TableHost>
          </ContentLayout>
        </SideSheetMain>
        <SideSheet>
          <ScopeChangeRequestDetailPanel vm={vm.changeRequestDetailsVM} />
        </SideSheet>
      </SideSheetHostView>
      <ScopeChangeEditCustomItemsDialog
        vm={vm.changeRequestDetailsVM.scopeChangeEditCustomItemsDialogVM}
      />
      <ScopeChangeEditInventoryItemsDialog
        vm={vm.changeRequestDetailsVM.scopeChangeEditInventoryItemsDialogVM}
      />
      <ScopeChangeRequestFinalizedDialog
        vm={vm.scopeChangeRequestFinalizedDialogVM}
      />
      <ScopeChangeRequestActionSelectionDialog
        vm={vm.changeRequestDetailsVM.scopeChangeRequestActionSelectionDialogVM}
      />
      <ScopeChangeRequestResolveDialog
        vm={vm.scopeChangeRequestResolveDialogVM}
      />
      <ScopeChangeRequestRejectDialog
        vm={vm.scopeChangeRequestRejectDialogVM}
      />
      <ScopeChangeRequestRejectOnBehalfOfClientDialog
        vm={vm.scopeChangeRequestRejectOnBehalfOfClientDialogVM}
      />
    </div>
  )
})
